import { message } from 'antd';
import { POINTS_RANGES } from './constants';
import { CouponRule, RULE_TYPES } from './types';

/**
 * Copy text to clipboard with fallback support
 */
export const copyText = (text: string | undefined, successMsg: string): void => {
    if (!text) return;

    if (navigator?.clipboard && window?.isSecureContext) {
        window.navigator.clipboard.writeText(text);
        message.success(successMsg);
    } else {
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'absolute';
        textArea.style.opacity = '0';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.select();
        new Promise(() => {
            document.execCommand('copy');
            textArea.remove();
        });
        message.success(successMsg);
    }
};

/**
 * Filter list by points range
 */
export const filterByPoints = <T extends { points: number }>(list: T[] | undefined, range: string): T[] => {
    if (!list) return [];

    const rangeConfig = POINTS_RANGES.find((r) => r.key === range);
    if (!rangeConfig) return list;

    return list.filter((item) => rangeConfig.filter(item.points));
};

/**
 * Check if should show shop button based on coupon type
 */
export const shouldShowShopBtn = (coupon: CouponRule | undefined): boolean => {
    if (!coupon) return false;

    const { ruleType, thirdUrl } = coupon;

    if (ruleType === RULE_TYPES.COUPONS) return true;
    if (ruleType === RULE_TYPES.THIRD_COUPONS && thirdUrl) return true;

    return false;
};

/**
 * Check if coupon button should be disabled
 */
export const isCouponDisabled = (
    rule: CouponRule,
    inBlackList: boolean,
    isLogin: boolean,
    userPoints: number | undefined
): boolean => {
    // In blacklist
    if (inBlackList) return true;

    // Not enough points (only check when logged in)
    if (isLogin && Number(rule?.points) > (userPoints || 0)) return true;

    // Out of stock (not for COUPONS type)
    if (rule?.ruleType !== RULE_TYPES.COUPONS && rule?.stock === 0) return true;

    return false;
};

/**
 * Check if should show stock indicator
 */
export const shouldShowStock = (rule: CouponRule): boolean => {
    return rule?.ruleType !== RULE_TYPES.COUPONS && !!rule?.stockPicEnable;
};

/**
 * Build login redirect URL
 */
export const buildLoginRedirectUrl = (locale: string, asPath: string, id?: string): string => {
    const hash = id ? `#${id}` : '';
    return `/${locale}/login?redirect=${encodeURIComponent(`/${locale}/${asPath}${hash}`)}`;
};

/**
 * Get store URL by locale
 */
export const getStoreUrl = (locale: string): string => {
    return `https://${locale}.ecoflow.com`;
};

/**
 * Navigate to URL, handle mobile vs desktop differently
 */
export const navigateToUrl = (url: string, isMobile: boolean, router: any): void => {
    if (isMobile) {
        router.push(url);
    } else {
        setTimeout(() => window.open(url, '_blank'));
    }
};
